import { mapActions, mapState } from "pinia";

import BUILDER_Z_INDEX from "%/dynamic-component-editor/constants/builder-z-index";
import { clampFixedMenuToViewport } from "%/dynamic-component-editor/utils/viewport-clamp";

// Lazy store resolution: a module-scope import here closes a cycle
// (stores -> components -> mixins -> classes). See scripts/find-import-cycles.py.
// eslint-disable-next-line global-require
const useDynamicComponentEditorStore = (...args) => require("%/dynamic-component-editor/stores/dynamic-component-editor").default(...args);


const ContextualMenuMixinStaging = {
  computed: {
    ...mapState(useDynamicComponentEditorStore, [
      "showContextualMenuForIdInBuilder",
      "showContextualMenuForIdInLayers",
      "isDynamicAppComponent",
      "contextualMenuPosition",
    ]),
    contextMenuFixedPositionStyle() {
      return {
        position: "fixed",
        zIndex: BUILDER_Z_INDEX.BUILDER_POPUP,
        ...this.contextualMenuPosition,
      };
    },
    showContextualMenu() {
      // `breakpoint` may be undefined, an empty string (legacy), or a
      // breakpoint object `{label, value, alias}` (canvas v2). Any
      // non-default breakpoint suppresses the menu so preview frames
      // don't open contextual actions; treat missing breakpoint OR the
      // default alias as "default breakpoint, menu allowed."
      const alias = typeof this.breakpoint === "string"
        ? this.breakpoint
        : this.breakpoint?.alias;
      const isDefaultBp = !alias || alias === "default";
      return this.showContextualMenuForIdInBuilder === this.identifier && isDefaultBp;
    },
  },
  methods: {
    ...mapActions(useDynamicComponentEditorStore, [
      "setShowContextualMenuForIdInBuilder",
      "setShowContextualMenuForIdInLayers",
      "isDeletable",
      "isEditable",
      "isDuplicatable",
      "canWrap",
      "canCreateCustomComponent",
    ]),
    openContextualMenuForBuilder(event) {
      if (!this.editMode || !event.target) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const internalIsDeletable = this.isDeletable(this.identifier, this.configuration);
      const internalIsEditable = this.isEditable(this.identifier, this.configuration);
      const internalIsDuplicatable = this.isDuplicatable(this.identifier, this.configuration);
      const internalCanWrap = this.canWrap(this.identifier, this.configuration);
      const internalCanCreateCustomComponent = this.canCreateCustomComponent(this.identifier, this.configuration);

      const showBasedOnLockOptions = internalIsEditable || internalIsDeletable || internalIsDuplicatable ||
          internalCanWrap || internalCanCreateCustomComponent;

      if (!showBasedOnLockOptions) {
        return;
      }

      const { clientX, clientY } = event;
      const store = useDynamicComponentEditorStore();
      store.contextualMenuPosition = { top: `${clientY}px`, left: `${clientX}px` };
      this.setShowContextualMenuForIdInLayers(null);
      this.setShowContextualMenuForIdInBuilder(this.identifier);

      // Correct against the menu's real size once mounted, same as the canvas/layers-panel menus.
      this.$nextTick(() => {
        const menuEl = this.$refs.contextualMenu?.$el;
        if (!menuEl) {
          return;
        }
        store.contextualMenuPosition = clampFixedMenuToViewport(clientX, clientY, menuEl.getBoundingClientRect());
      });
    },
  },
};

export { ContextualMenuMixinStaging };
